// src/screens/admin/stockAlerts/StockAlerts.jsx
//
// Data: local useState dummy data
// To replace with API: swap DUMMY_MATERIALS with your API call in useEffect

import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Search,
  Package,
  ArrowLeft,
} from "lucide-react";
import RestockMaterial from "../../../subscreen/admin/restockMaterial/RestockMaterial";
import "../../../assets/css/admin/stockAlerts/StockAlerts.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const getStockStatus = ({ currentStock, minimumStock}) => {
  if (currentStock <= minimumStock / 2) return "critical";
  if (currentStock <= minimumStock) return "warning";
  return "good";
};

const STATUS_CONFIG = {
  critical: {
    label: "Critical",
    badgeClass: "sa-badge-critical",
    rowClass: "sa-row-critical",
    iconClass: "sa-icon-critical",
    Icon: AlertCircle,
  },
  warning: {
    label: "Low Stock",
    badgeClass: "sa-badge-warning",
    rowClass: "sa-row-warning",
    iconClass: "sa-icon-warning",
    Icon: AlertTriangle,
  },
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const StockAlerts = () => {
  const navigate = useNavigate();
  // ── To replace with API: useEffect mein fetch karo, setMaterials(data) ──
  const [materials, setMaterials] = useState([]);
  const {baseUrl, authToken} = useContext(AppContext);
  const [restockId, setRestockId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const viewStockAlerts = () => {
    console.log("View Stock alerts called!");
    axios.get(`${baseUrl}/admin/inventory/viewStockAlerts`, {
      headers: {
        Authorization: `Bearer${authToken}`
      }
    })
    .then((response) => {
      console.log(response.data);
      setMaterials(response.data.alerts); 
    }).catch((error) => {
      console.log(error);
    })
  }

  useEffect(() => {
    viewStockAlerts();
  },[]);

  const alertMaterials = materials.filter((m) => getStockStatus(m) !== "good");
  const criticalCount = alertMaterials.filter(
    (m) => getStockStatus(m) === "critical",
  ).length;
  const warningCount = alertMaterials.filter(
    (m) => getStockStatus(m) === "warning",
  ).length;

  const filtered = alertMaterials.filter((m) => {
    const matchesFilter =
      activeFilter === "all" || getStockStatus(m) === activeFilter;
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      m.name.toLowerCase().includes(q) ||
      m.adcategory?.name.toLowerCase().includes(q) ||
      m.supplier.toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  const getShortage = (m) => Math.max(0, m.reorderPoint - m.currentStock);

  return (
    <div className="sa-container">
      {/* ── Back Button ── */}
      <button className="sa-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </button>

      {/* ── Header ── */}
      <div className="sa-header">
        <h1 className="sa-title">Stock Alerts</h1>
        <p className="sa-subtitle">
          Materials that require immediate attention
        </p>
      </div>

      {/* ── Stats Cards ── */}
      <div className="sa-stats-grid">
        <div className="sa-stat-card">
          <div className="sa-stat-icon sa-icon-total">
            <Package size={22} />
          </div>
          <div className="sa-stat-content">
            <div className="sa-stat-value">{alertMaterials.length}</div>
            <div className="sa-stat-label">Total Alerts</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon sa-icon-critical">
            <AlertCircle size={22} />
          </div>
          <div className="sa-stat-content">
            <div className="sa-stat-value">{criticalCount}</div>
            <div className="sa-stat-label">Critical</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon sa-icon-warning">
            <AlertTriangle size={22} />
          </div>
          <div className="sa-stat-content">
            <div className="sa-stat-value">{warningCount}</div>
            <div className="sa-stat-label">Low Stock</div>
          </div>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="sa-controls">
        <div className="sa-filters">
          {[
            { key: "all", label: "All Alerts", count: alertMaterials.length },
            { key: "critical", label: "Critical", count: criticalCount },
            { key: "warning", label: "Low Stock", count: warningCount },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`sa-filter-btn ${activeFilter === key ? "active" : ""}`}
              onClick={() => setActiveFilter(key)}
            >
              {label}
              <span className="sa-filter-count">{count}</span>
            </button>
          ))}
        </div>

        <div className="sa-search">
          <Search size={15} className="sa-search-icon" />
          <input
            type="text"
            className="sa-search-input"
            placeholder="Search by name, category, supplier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="sa-table-container">
        {filtered.length === 0 ? (
          <div className="sa-empty">
            <span className="sa-empty-icon">✅</span>
            <h3>
              {alertMaterials.length === 0
                ? "All stocks are healthy!"
                : "No results found"}
            </h3>
            <p>
              {alertMaterials.length === 0
                ? "No materials need attention right now"
                : "Try adjusting your search or filter"}
            </p>
          </div>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Category</th>
                <th>Current Stock</th>
                <th>Min Stock</th>
                <th>Supplier</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const status = getStockStatus(m);
                const config = STATUS_CONFIG[status];
                const shortage = getShortage(m);
                return (
                  <tr key={m.id} className={config.rowClass}>
                    <td>
                      <div className="sa-material">
                        <div className={`sa-material-icon ${config.iconClass}`}>
                          <config.Icon size={16} />
                        </div>
                        <span className="sa-material-name">{m.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="sa-category">{m.adcategory?.name}</span>
                    </td>
                    <td>
                      <span className={`sa-stock-pill sa-stock-${status}`}>
                        {m.currentStock} {m.unit}
                      </span>
                    </td>
                    <td className="sa-min-stock">
                      {m.minimumStock} {m.unit}
                    </td>
                    <td className="sa-supplier">{m.supplier}</td>
                    <td>
                      <span className={`sa-status-badge ${config.badgeClass}`}>
                        {config.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal ── */}
      {restockId && (
        <RestockMaterial
          isOpen
          onClose={() => setRestockId(null)}
          materialId={restockId}
        />
      )}
    </div>
  );
};

export default StockAlerts;